import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { SERVER_ROOT } from 'env';
import { readDefaultGeminiLanguage } from 'canvas/utils/geminiLanguageStorage';

export const usePersonStore = create(
  persist(
    (set, get) => ({
        //res.send({person, activities, userCanvasMapping, type: 'PRIVATE'})
        person: null, 
        activities: [], 
        keyword: null,
        /** @deprecated use library navigation instead */
        activitySortHashtag: null,
        libraryViewMode: 'canvas',
        librarySection: 'all',
        librarySearchQuery: '',
        userCanvasMapping: null, 
        type: null, 
        loading: false, 
        hasMore: false, 
        index: 0,
        canvasJson: { canvas: null, isOwner: false },
        boardJson: null,


        loadPerson: async (personName, onlyPerson, options = {}) => {
            const store = get();
            const samePerson =
              store.person?.username &&
              personName &&
              store.person.username.toLowerCase() === String(personName).toLowerCase();
            if (!options.force && samePerson && !store.keyword) return;
            set({ loading: true, activities: [], keyword: null, activitySortHashtag: null });
            const data = await fetch(SERVER_ROOT + `/api/activity/init?personName=${personName}&keywordName=${null}&onlyPerson=${onlyPerson}`, { credentials: 'include', method: 'GET', headers: { 'Content-Type': 'application/json'} }).then(res => res.json());
            const { person, activities, userCanvasMapping, type } = data;
            console.log('loadPerson data is', data);
            const next = {
                person,
                activities,
                type,
                loading: false,
                index: 0,
            };
            // onlyPerson responses omit userCanvasMapping — keep the existing list
            // so the sidebar does not collapse to a single board.
            if (userCanvasMapping != null) {
                next.userCanvasMapping = userCanvasMapping;
            } else if (!samePerson) {
                next.userCanvasMapping = null;
            }
            set(next);
        },

        setActivitySortHashtag: hashtagName => {
            set({ activitySortHashtag: hashtagName || null });
        },

        setLibraryViewMode: mode => {
            if (mode === 'gallery' || mode === 'canvas') {
                set({ libraryViewMode: mode });
            }
        },

        setLibrarySection: section => {
            set({ librarySection: section || 'all' });
        },

        setLibrarySearchQuery: query => {
            set({ librarySearchQuery: query || '' });
        },

        prependCanvasActivity: (activity, canvasName) => {
            if (!activity) return;
            const store = get();
            if (store.keyword && canvasName && store.keyword !== canvasName) return;
            const activityKey = String(activity._id || activity.actionId || '');
            if (!activityKey) return;
            const exists = store.activities.some(
                a => String(a._id || a.actionId || '') === activityKey
            );
            if (exists) return;
            const activities = [activity, ...store.activities];
            const next = { activities };
            if (store.canvasJson?.canvas) {
                next.canvasJson = {
                    ...store.canvasJson,
                    canvas: {
                        ...store.canvasJson.canvas,
                        activities: [activity, ...(store.canvasJson.canvas.activities || [])],
                    },
                };
            }
            set(next);
        },
        loadBoard: async (personName, keyword) => {
            const store = get();
            if (store.person && store.keyword === keyword && store.activities.length > 0) {
                set({ loading: false });
                return;
            }
            set({ loading: true });
            if (!store.person) await store.loadPerson(personName, true);
            const params = new URLSearchParams({
                person_name: personName,
                hashtag: keyword,
                locale: readDefaultGeminiLanguage(),
            });
            const data = await fetch(`${SERVER_ROOT}/api/board?${params}`, {
                method: 'GET',
                credentials: 'include',
            }).then(res => res.json());
            set({
                boardJson: data.board || null,
                activities: data.activities ?? [],
                keyword,
                loading: false,
            });
        },
        /** @deprecated use loadBoard */
        loadCanvas: async (personName, keyword) => {
            return get().loadBoard(personName, keyword);
        },
        deleteActivity: (activity) => {
            fetch(SERVER_ROOT + `/api/activity?activityId=${activity._id}`, { method: 'DELETE', credentials: 'include',  }).then(res => res.json());
            const store = get();
            const newActivities = store.activities.filter(a => a._id !== activity._id);
            set({ activities: newActivities });
            if(store.canvasJson.canvas) {
                const canvasActivities = store.canvasJson.canvas.activities.filter(a => a._id !== activity._id);
                set({ canvasJson: { ...store.canvasJson, canvas: { ...store.canvasJson.canvas, activities: canvasActivities } } });
            }
        },
        deleteCanvas: (canvasName) => {
            fetch(SERVER_ROOT + `/api/board?hashtag=${encodeURIComponent(canvasName)}`, {
                method: 'DELETE',
                credentials: 'include',
            }).then(res => res.json()).catch(() => {});
            get().removeBoardLocal(canvasName);
        },

        removeBoardLocal: (canvasName) => {
            const store = get();
            if (!canvasName) return;
            const name = String(canvasName);
            const lowered = name.toLowerCase();
            const next = {};
            if (store.person?.hashtags) {
                next.person = {
                    ...store.person,
                    hashtags: store.person.hashtags.filter(
                        h => (h.loweredName || h.name || '').toLowerCase() !== lowered
                    ),
                };
            }
            if (store.userCanvasMapping?.canvasMappings) {
                next.userCanvasMapping = {
                    ...store.userCanvasMapping,
                    canvasMappings: (store.userCanvasMapping.canvasMappings || []).filter(
                        m => String(m.name || '').toLowerCase() !== lowered
                    ),
                    currentCanvasMappings: (store.userCanvasMapping.currentCanvasMappings || []).filter(
                        m => String(m.name || '').toLowerCase() !== lowered
                    ),
                };
            }
            if (store.keyword && String(store.keyword).toLowerCase() === lowered) {
                next.keyword = null;
                next.boardJson = null;
                next.activities = [];
            }
            if (Object.keys(next).length) set(next);
        },

        addUserHashtag: hashtag => {
            if (!hashtag?.name) return;
            const store = get();
            if (!store.person) return;
            const existing = store.person.hashtags || [];
            if (existing.some(h => h.name === hashtag.name || h.loweredName === hashtag.loweredName)) {
                return;
            }
            const nextHashtag = {
                name: hashtag.name,
                color: hashtag.color || '#6366f1',
                loweredName: hashtag.loweredName || hashtag.name.toLowerCase(),
            };
            const next = {
                person: {
                    ...store.person,
                    hashtags: [...existing, nextHashtag],
                },
            };
            if (store.userCanvasMapping) {
                const mappings = store.userCanvasMapping.canvasMappings || [];
                if (!mappings.some(m => m.name === nextHashtag.name)) {
                    next.userCanvasMapping = {
                        ...store.userCanvasMapping,
                        canvasMappings: [
                            ...mappings,
                            {
                                type: 'CANVAS',
                                name: nextHashtag.name,
                                canvasId: null,
                                activityCount: 0,
                                color: nextHashtag.color,
                                screenshotUrl: null,
                            },
                        ],
                    };
                }
            }
            set(next);
        },

        followPerson: async personId => {
            const res = await fetch(SERVER_ROOT + `/api/follow?person_id=${personId}`, { credentials: 'include' });
            if (!res.ok) throw new Error(res.statusText);
            const store = get();
            if (!store.person) return;
            set({ person: { ...store.person, followStatus: [{ status: 'FOLLOW' }] } });
        },

        unfollowPerson: async personId => {
            const res = await fetch(SERVER_ROOT + `/api/unfollow?person_id=${personId}`, { credentials: 'include' });
            if (!res.ok) throw new Error(res.statusText);
            const store = get();
            if (!store.person) return;
            set({ person: { ...store.person, followStatus: [] } });
        },
    }),
    {
        name: 'hashely-person-store',
        storage: createJSONStorage(() => localStorage),
        partialize: state => ({
            libraryViewMode: state.libraryViewMode,
        }),
    }
  )
);

export const useUserStore = create((set) => {
});

export const useSearchStore = create((set) => {
});

export const useNewsfeedStore = create((set) => {
})
